import { useState, useMemo, useEffect } from "react";
import { useLocation } from "wouter";
import { useLogin } from "@orbit/api-client-react";
import { canonicalizePhone, formatPhDisplay } from "../lib/phone";
import { useAuth } from "../hooks/useAuth";
import { AuthLayout } from "../components/AuthLayout";
import { consumeSessionExpiredFlag } from "../lib/auth";

export function LoginPage() {
  const [, navigate] = useLocation();
  const { signIn } = useAuth();
  const [phoneRaw, setPhoneRaw] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Phase 4 edge case: if we landed here because a refresh attempt
  // failed (silent refresh hook fell through to onUnauthorized), show a
  // friendly banner so the user knows why they're being asked to sign in.
  const [sessionExpired, setSessionExpired] = useState(false);
  useEffect(() => {
    if (consumeSessionExpiredFlag()) setSessionExpired(true);
  }, []);

  const loginMut = useLogin();
  const canonical = useMemo(() => canonicalizePhone(phoneRaw), [phoneRaw]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!canonical) {
      setError("Please enter a valid PH mobile number.");
      return;
    }
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    try {
      const r = await loginMut.mutateAsync({ phone: canonical, password });
      signIn(r.customerId, r.tokens);
      navigate("/cards");
    } catch (err) {
      const e = err as { status?: number; apiError?: { message?: string; code?: string } };
      // The server returns a uniform 401 INVALID_CREDENTIALS for both
      // "no such phone" and "wrong password" — we mirror that uniformity
      // in the UI so the form never tells an attacker which phones are
      // registered. 429s come from the brute-force limiter. A distinct
      // 403 FORBIDDEN comes from Phase 33 (D105) — credentials matched
      // but the admin has suspended the account; we surface a friendly
      // "contact support" message rather than the opaque 401.
      if (e.status === 403) {
        setError(
          e.apiError?.message ??
            "This account has been suspended. Please contact support.",
        );
      } else if (e.status === 401) {
        setError("That phone number and password don't match. Try again.");
      } else if (e.status === 429) {
        setError("Too many attempts. Try again in a few minutes.");
      } else {
        setError(e.apiError?.message ?? "Couldn't sign you in. Try again.");
      }
    }
  };

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back to your Orbit.">
      {sessionExpired && (
        <div className="mb-4 rounded-card border border-amber-light bg-amber-xlight px-3 py-2 text-sm text-navy">
          Your session expired. Please sign in again to continue.
        </div>
      )}
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="phone" className="label">Mobile number</label>
          <input
            id="phone"
            className="input"
            value={formatPhDisplay(phoneRaw)}
            onChange={(e) => setPhoneRaw(e.target.value)}
            placeholder="0917-123-4567"
            inputMode="tel"
            autoComplete="tel"
            required
          />
        </div>
        <div>
          <label htmlFor="password" className="label">Password</label>
          <div className="relative">
            <input
              id="password"
              className="input pr-16"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-2 my-auto text-xs font-medium text-navy underline"
              tabIndex={-1}
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        {error && <p className="text-red-600 text-sm">{error}</p>}
        {/* Phase 31 (D103) — entry point to the public reset flow.
            Lives below the password field where users naturally look
            after a failed attempt. */}
        <p className="-mt-2 text-right text-sm">
          <a
            href="/forgot-password"
            className="text-navy underline"
            data-testid="link-forgot-password"
          >
            Forgot password?
          </a>
        </p>
        <button
          type="submit"
          className="btn-primary w-full"
          disabled={!canonical || !password || loginMut.isPending}
        >
          {loginMut.isPending ? "Signing in…" : "Sign in"}
        </button>
        <p className="text-center text-sm text-ink-dark">
          New to Orbit?{" "}
          <a
            href="/signup"
            className="underline text-navy"
            data-testid="link-create-orbit-from-login"
          >
            Create your Orbit
          </a>
        </p>
      </form>
    </AuthLayout>
  );
}
